import { t } from "@lingui/macro";
import { BoxProps, useResponsiveProp, useTheme } from "@shopify/restyle";
import { LinearGradient } from "expo-linear-gradient";
import { MotiView } from "moti";
import React, {
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import {
  FlatList,
  LayoutChangeEvent,
  ListRenderItemInfo,
  ViewToken,
} from "react-native";

import { Loader } from "@whoosh/design-system/src/components/display/Loader";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { Theme } from "../../../theme/restyle/theme";
import { Button } from "../../controls";
import { ChevronMiniLeft } from "../../icons_v2/general/icons/ChevronMiniLeft";
import { ChevronMiniRight } from "../../icons_v2/general/icons/ChevronMiniRight";
import { Box, HStack, Pressable } from "../../primitive";

export interface LayoutRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
  left: number;
  top: number;
}

type PaddingHorizontalProp<Th extends Theme = Theme> = Pick<
  BoxProps<Th>,
  "paddingHorizontal"
>;

type CardScrollerProps<T> = {
  data: T[];
  renderItem: (item: T, index: number) => React.JSX.Element | null;
  onEndReached?: () => void;
  hasMoreItems?: boolean;
  isLoading?: boolean;
  titleElement?: React.JSX.Element;
  hideMargin?: boolean;
} & PaddingHorizontalProp;

export const CardScroller = <T extends { id: string }>({
  data,
  renderItem,
  paddingHorizontal,
  onEndReached,
  hasMoreItems,
  isLoading,
  titleElement,
  hideMargin,
}: CardScrollerProps<T>) => {
  const theme = useTheme();
  const paddingHorizontalConverted = useResponsiveProp(
    paddingHorizontal ?? { base: 0 }
  );
  const paddingHorizontalValue = theme.spacing[paddingHorizontalConverted];
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);
  const [contentWidth, setContentWidth] = useState<number | null>(null);

  const scrollViewRef = useRef<any>(null);
  const isScrollingRef = useRef(false);

  const [endIndex, setEndIndex] = useState(0);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [arrowsVisible, setArrowsVisible] = useState(false);

  /**
   * This bundle of state / ref / function handles ensuring
   * that all the items in the scrollview are the same height.
   *
   * We cannot simply use height 100% as that does not work on
   * native. We must find the max height and actually set it.
   */
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const measuredItems = useRef<number>(0);
  const handleOnLayout = (event: LayoutChangeEvent) => {
    const height = event.nativeEvent.layout.height;
    if (height > maxHeight) {
      setMaxHeight(height);
    }
    measuredItems.current += 1;

    // Once all items are measured, set the max height to all items
    if (measuredItems.current === data.length) {
      setMaxHeight(height);
    }
  };

  const isLargeScreen = useBreakpointValue({
    xs: false,
    sm: false,
    md: false,
    lg: true,
  });

  const [currentPage, setCurrentPage] = useState(1);

  const [visibleItemsCount, setVisibleItemsCount] = useState<number | null>(
    null
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (isScrollingRef.current) return;
      isScrollingRef.current = true;
      setCurrentPage(page);
      if (visibleItemsCount && scrollViewRef.current) {
        const newScrollIndex = (page - 1) * visibleItemsCount;
        setScrollIndex(newScrollIndex);
        scrollViewRef.current.scrollToIndex({
          animated: true,
          index: newScrollIndex,
        });
      }
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 300);
    },
    [
      visibleItemsCount,
      scrollViewRef,
      isScrollingRef,
      setCurrentPage,
      setScrollIndex,
    ]
  );

  const numberOfPages =
    visibleItemsCount && data.length
      ? Math.ceil(data.length / visibleItemsCount)
      : 1;

  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken<T>[] }) => {
      try {
        if (isScrollingRef.current) return;
        setVisibleItemsCount(viewableItems.length);
        if (viewableItems.length > 0) {
          const firstVisibleIndex = viewableItems[0].index ?? 0;
          const newPage =
            Math.floor(firstVisibleIndex / viewableItems.length) + 1;
          setCurrentPage(newPage);
        }
      } catch (error) {
        console.log(error);
      }
    }
  ).current;

  const scrollLeft = () => {
    if (isScrollingRef.current || scrollIndex <= 0) return;
    isScrollingRef.current = true;
    const newScrollIndex = scrollIndex - 1;
    setScrollIndex(newScrollIndex);
    if (visibleItemsCount && scrollViewRef.current) {
      const newPage = Math.floor(newScrollIndex / visibleItemsCount) + 1;
      setCurrentPage(newPage);
      scrollViewRef.current.scrollToIndex({
        animated: true,
        index: newScrollIndex,
      });
    }
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 300);
  };

  const scrollRight = () => {
    if (isScrollingRef.current || scrollIndex >= data.length - 1) return;
    isScrollingRef.current = true;
    const newScrollIndex = scrollIndex + 1;
    setScrollIndex(newScrollIndex);
    if (visibleItemsCount && scrollViewRef.current) {
      const newPage = Math.floor(newScrollIndex / visibleItemsCount) + 1;
      setCurrentPage(newPage);
      scrollViewRef.current.scrollToIndex({
        animated: true,
        index: newScrollIndex,
      });
    }
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 300);
  };

  useLayoutEffect(() => {
    if (contentWidth !== null && layoutWidth !== null) {
      setArrowsVisible(contentWidth > layoutWidth);
    }
  }, [contentWidth, layoutWidth]);

  const isArrowBackDisabled = scrollIndex === 0;

  const isArrowForwardDisabled =
    (scrollIndex === endIndex && scrollIndex !== 0) || isLoading;
  // check if we are within 3 items of the end, but here is number 6, because we usually showing 3 items at once on screen already

  const isWithinThreeItems = data.length - scrollIndex <= 6;

  useEffect(() => {
    if (onEndReached && hasMoreItems && isWithinThreeItems && data.length > 0) {
      onEndReached();
    }
  }, [onEndReached, hasMoreItems, data, isWithinThreeItems, scrollIndex]);

  const pageDots = useMemo(
    () =>
      Array.from({ length: numberOfPages }).map((_, index) => (
        <Pressable key={index} onPress={() => handlePageChange(index + 1)}>
          <Box
            style={{
              width: 6,
              height: 6,
              borderRadius: 9999,
            }}
            backgroundColor={
              currentPage === index + 1 ? "grayscale-1000" : "grayscale-100"
            }
          />
        </Pressable>
      )),
    [numberOfPages, currentPage, handlePageChange]
  );

  return (
    <Box>
      <HStack
        minHeight={{ base: "auto", lg: 56 }}
        alignItems="center"
        justifyContent={titleElement ? "space-between" : "flex-end"}
      >
        {titleElement || null}
        {!arrowsVisible ? null : (
          <HStack gap={2} pr={{ base: 6, md: 0 }}>
            <Button
              variant="secondary"
              size="tiny"
              onPress={scrollLeft}
              isDisabled={isArrowBackDisabled}
              leftIcon={<ChevronMiniLeft />}
              accessibilityLabel={t`Previous button`}
            />
            <Button
              variant="secondary"
              size="tiny"
              onPress={scrollRight}
              isDisabled={isArrowForwardDisabled}
              rightIcon={<ChevronMiniRight />}
              accessibilityLabel={t`Next button`}
            />
          </HStack>
        )}
      </HStack>

      {data ? (
        <Box marginBottom={hideMargin ? 0 : 14}>
          {/* linear gradient left */}
          <MotiView
            pointerEvents="none"
            style={{
              position: "absolute",
              height: "100%",
              zIndex: 100,
            }}
            from={{ opacity: 0 }}
            animate={{
              opacity: scrollIndex > 0 && isLargeScreen ? 1 : 0,
            }}
            transition={{
              opacity: {
                type: "timing",
                duration: 300,
              },
            }}
          >
            <Box width={160} height="100%" pointerEvents="none">
              <LinearGradient
                // Background Linear Gradient
                colors={["#FFFFFF", "#FFFFFF00"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{
                  position: "absolute",
                  height: "100%",
                  width: "100%",
                  zIndex: 100,
                }}
              />
            </Box>
          </MotiView>
          {/* linear gradient right */}
          <MotiView
            pointerEvents="none"
            style={{
              position: "absolute",
              height: "100%",
              zIndex: 100,
              right: 0,
            }}
            from={{ opacity: 0 }}
            animate={{
              opacity:
                !(scrollIndex === endIndex && scrollIndex !== 0) &&
                isLargeScreen
                  ? 1
                  : 0,
            }}
            transition={{
              opacity: {
                type: "timing",
                duration: 300,
              },
            }}
          >
            <Box width={160} height="100%" pointerEvents="none">
              <LinearGradient
                // Background Linear Gradient
                colors={["#FFFFFF00", "#FFFFFF"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{
                  position: "absolute",
                  height: "100%",
                  width: "100%",
                  zIndex: 100,
                }}
              />
            </Box>
            {isLoading ? <Loader /> : null}
          </MotiView>

          <FlatList
            contentContainerStyle={{ paddingRight: isLargeScreen ? 0 : 16 }}
            testID="card-scroller"
            data={data}
            horizontal
            style={{
              marginTop: hideMargin ? 0 : 20,
              paddingHorizontal: paddingHorizontalValue ?? 0,
              backgroundColor: "white",
            }}
            ref={scrollViewRef}
            onLayout={(event: LayoutChangeEvent) => {
              setLayoutWidth(event?.nativeEvent?.layout?.width ?? null);
            }}
            onContentSizeChange={(w: number) => {
              setContentWidth(w);
            }}
            onViewableItemsChanged={onViewableItemsChanged}
            viewabilityConfig={{
              itemVisiblePercentThreshold: 50,
            }}
            ItemSeparatorComponent={() => <Box width={8} />}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item, index) => `${item.id ?? index}`}
            renderItem={({ item, index }: ListRenderItemInfo<T>) => {
              return (
                <Box
                  height={maxHeight ? maxHeight : "auto"}
                  onLayout={handleOnLayout}
                >
                  {renderItem(item, index)}
                </Box>
              );
            }}
            onEndReached={() => {
              setEndIndex(scrollIndex);
            }}
          />
          <HStack justifyContent="center" gap={1} mt={2} width="100%">
            {pageDots}
          </HStack>
        </Box>
      ) : null}
    </Box>
  );
};
